<script setup lang="ts">
import { loadMyPropertyPreferences, myPropertyPreferences, saveMyPropertyPreferences } from '@/appLogic/view/preferences';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { openFailToSavedModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import ToggleSwitch from '@/corePagesModule/components/ToggleSwitch.vue';
import { diContainer } from '@/coreContainer/container';
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { computed, onBeforeMount, reactive } from 'vue';
import { useRouter } from 'vue-router';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { VPopover } from 'v-tooltip';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';

const t = i18n.t;
const router = useRouter();

/**
 * このページで使用するStateのinterface
 */
interface SettingLogicState {
  clientSettingState: {
    isEdited: boolean;
    settings: Record<string, boolean | undefined>;
  };
  serverSettingState: {
    isEdited: boolean;
    settings: Record<string, boolean | undefined>;
  };
  isFetchingErrorOccured: boolean;
}

const state = reactive<SettingLogicState>({
  clientSettingState: {
    isEdited: false,
    settings: { unpaidOrderedOrderAppearance: undefined, orderReminderAppearance: undefined },
  },
  serverSettingState: {
    isEdited: false,
    settings: { useRoundDown: undefined, useCurrentMonthSalesResult: undefined },
  },
  isFetchingErrorOccured: false,
});

const isEdited = computed(() => state.clientSettingState.isEdited || state.serverSettingState.isEdited);

/**
 * isEditedをリセット
 */
const resetIsEdited = () => {
  state.clientSettingState.isEdited = false;
  state.serverSettingState.isEdited = false;
};

/**
 * 設定のトグルを押した際のハンドラ
 * @param toggledKey - トグルを押した項目
 */
const onToggleClientSetting = (toggledKey: keyof SettingLogicState['clientSettingState']['settings']) => {
  if (!(toggledKey in myPropertyPreferences.order)) {
    return;
  }
  state.clientSettingState.isEdited = true;
  state.clientSettingState.settings[toggledKey] = !state.clientSettingState.settings[toggledKey];
};

/**
 * トグルを押した際のハンドラ
 * @param type - 押したトグル
 */
const onToggleServerSetting = (type: keyof SettingLogicState['serverSettingState']['settings']) => {
  state.serverSettingState.isEdited = true;
  state.serverSettingState.settings[type] = !state.serverSettingState.settings[type];
};

/**
 * 保存ボタンを押した際の処理
 */
const onSave = async () => {
  if (
    state.serverSettingState.isEdited &&
    !(await showInfoModal({
      modalTitle: t('組織全体の設定が変更されています'),
      modalDescription: t('シミュレーション設定は組織内全てのユーザーに対して反映されます。よろしいですか？'),
      nextButtonLabel: t('続行する'),
      backButtonLabel: t('キャンセル'),
    }))
  )
    return;

  for (const [key, value] of Object.entries(state.clientSettingState.settings)) {
    if (value == null) continue;
    myPropertyPreferences.order[key] = value;
  }
  const results = await waitLoading(Promise.all([saveMyPropertyPreferences(), saveToRDS()]), t('保存中…'), '');

  if (results == null) {
    await openFailToSavedModal();
    return;
  }

  const [indexedDbResult, rdsResult] = results;
  const clientSettingsSaved = !state.clientSettingState.isEdited || indexedDbResult != null;
  const serverSettingsSaved = !state.serverSettingState.isEdited || rdsResult?.status.type === 'success';
  const simulationStatus = rdsResult?.status.type === 'success' ? rdsResult.status.data.simulationStatus : undefined;
  if (simulationStatus === 'failed' || simulationStatus === 'unknown') {
    const simulationMessage =
      simulationStatus === 'failed'
        ? t(
            '設定変更後のシミュレーションに失敗しました。予在庫や発注推奨数量に変更が反映されていない可能性があります。しばらく時間をおいて再度設定を保存してください。解決しない場合はお問い合わせください。'
          )
        : t(
            '設定変更後のシミュレーション完了を確認できませんでした。シミュレーション処理が継続している可能性があるため、しばらく時間をおいて結果をご確認ください。変更が適用されていない場合、再度設定を保存してください。解決しない場合はお問い合わせください。'
          );
    await showInfoModal({
      modalTitle:
        simulationStatus === 'failed' ? t('シミュレーションの実行に失敗しました') : t('シミュレーションの完了を確認できませんでした'),
      modalDescription: clientSettingsSaved ? simulationMessage : `${simulationMessage}\n${t('一部の設定を保存できませんでした')}`,
      backButtonLabel: '',
      nextButtonLabel: t('OK'),
      enableOutsideClick: false,
      isCenterContent: true,
      useKeyboard: false,
    });
  } else if (clientSettingsSaved && serverSettingsSaved) {
    await openSuccessToSaveModal();
    location.reload();
  } else if (indexedDbResult == null && rdsResult == null) {
    await openFailToSavedModal();
  } else {
    showInfoModal({
      modalTitle: t('一部の設定を保存できませんでした'),
      modalDescription: t('ページを再読み込みして、もう一度お試しください。'),
      backButtonLabel: '',
      nextButtonLabel: t('OK'),
      enableOutsideClick: false,
      isCenterContent: true,
      useKeyboard: false,
    });
  }
  if (clientSettingsSaved) state.clientSettingState.isEdited = false;
  if (serverSettingsSaved) state.serverSettingState.isEdited = false;
};

/**
 * RDSに設定を保存する
 */
const saveToRDS = async () => {
  if (
    state.serverSettingState.isEdited &&
    !state.isFetchingErrorOccured &&
    state.serverSettingState.settings.useRoundDown != null &&
    state.serverSettingState.settings.useCurrentMonthSalesResult != null
  ) {
    return await diContainer.putSimulationSettingUseCase.execute({
      useRoundDown: state.serverSettingState.settings.useRoundDown,
      useCurrentMonthSalesResult: state.serverSettingState.settings.useCurrentMonthSalesResult,
    });
  }
  return null;
};

/**
 * ダッシュボードアラート設定ボタン押下
 */
const onClickDashboardAlertSetting = () => {
  router.push('logic/dashboard-alert');
};

onBeforeMount(async () => {
  resetIsEdited();

  const [_, simulationSettingResult] = (await waitLoading(
    Promise.all([loadMyPropertyPreferences(), diContainer.getSimulationSettingUseCase.execute()]),
    undefined,
    ''
  )) ?? [null, null];

  for (const key of Object.keys(state.clientSettingState.settings)) {
    if (key in myPropertyPreferences.order) {
      state.clientSettingState.settings[key] = myPropertyPreferences.order[key] ?? true;
    }
  }

  // サーバーから設定を取得
  if (simulationSettingResult != null && simulationSettingResult.status.type === 'success') {
    state.serverSettingState.settings.useRoundDown = simulationSettingResult.status.data.useRoundDown;
    state.serverSettingState.settings.useCurrentMonthSalesResult = simulationSettingResult.status.data.useCurrentMonthSalesResult;
  } else {
    state.isFetchingErrorOccured = true;
    showInfoModal({
      modalTitle: t('一部設定の取得に失敗しました'),
      modalDescription: t(
        'しばらく時間をおいてから、ページを再読み込みしてください。再読み込みしても取得できない場合は、お問い合わせください。'
      ),
    });
  }
});
</script>
<template>
  <section class="setting-logic-page">
    <header class="setting-logic-page__header">
      <SettingsPageTitle :label="t('設定 > 発注・計算ロジック')" />
    </header>
    <div class="container">
      <div class="setting-content">
        <ToggleSwitch
          :is-enabled="state.clientSettingState.settings.unpaidOrderedOrderAppearance"
          @on-toggled="onToggleClientSetting('unpaidOrderedOrderAppearance')"
        >
          {{ t('最新在庫の日付以前の納期を持つ未入荷の発注を検知した場合、アラートで通知する') }}
        </ToggleSwitch>
        <ToggleSwitch
          :is-enabled="state.clientSettingState.settings.orderReminderAppearance"
          @on-toggled="onToggleClientSetting('orderReminderAppearance')"
        >
          {{ t('発注登録・保存後にリマインド設定を表示する') }}
        </ToggleSwitch>
        <div class="dashboard-alert">
          {{ t('ダッシュボードのアラート設定') }}
          <TextButton button-style="btn-secondary" @click="onClickDashboardAlertSetting">{{ t('詳細設定') }}</TextButton>
        </div>
        <h3>
          {{ t('シミュレーション設定（組織単位）') }}
        </h3>
        <ToggleSwitch
          :is-enabled="state.serverSettingState.settings.useRoundDown"
          :disabled="state.isFetchingErrorOccured"
          @on-toggled="onToggleServerSetting('useRoundDown')"
        >
          {{ t('発注推奨数量の端数を切り捨てる（オフの場合は切り上げ）') }}
        </ToggleSwitch>
        <div>
          <ToggleSwitch
            :is-enabled="state.serverSettingState.settings.useCurrentMonthSalesResult"
            :disabled="state.isFetchingErrorOccured"
            @on-toggled="onToggleServerSetting('useCurrentMonthSalesResult')"
          >
            <div class="simulation-sales-result-wdesc">
              {{ t('シミュレーションで当月の販売実績を考慮する') }}
              <VPopover placement="bottom-start" :offset="4" :auto-hide="true" class="simulation-sales-result-wdesc__popover">
                <button class="simulation-sales-result-wdesc__button">
                  <Mdi icon="information" variant="outlined" />
                </button>

                <template #popover>
                  <div class="simulation-sales-result-popover">
                    {{
                      `${t('オンにすると、登録済みの当月販売実績を反映してシミュレーションします。')}\n${t(
                        '現在の販売状況に応じて、予在庫推移や発注推奨数量が変わる場合があります。'
                      )}\n${t('当月販売実績が登録されていない場合、シミュレーション結果は変わりません。')}`
                    }}
                  </div>
                </template>
              </VPopover>
            </div>
          </ToggleSwitch>
        </div>
      </div>
      <div class="save">
        <span class="save__text" :class="{ invisible: !isEdited }">{{ t('未保存の設定変更があります') }}</span>
        <TextButton @click="onSave">{{ t('保存する') }}</TextButton>
      </div>
    </div>
  </section>
</template>
<style lang="scss" scoped>
h3 {
  border-top: 1px $borderDefault solid;
  padding-top: 24px;
  font-size: 16px;
  font-weight: 700;
}
.setting-logic-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }
}
.container {
  background-color: white;
  padding: 24px 16px 40px 18px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: auto;
}
.setting-content {
  display: grid;
  gap: 16px;
  padding-bottom: 32px;

  &--separate {
    padding-top: 32px;
    border-top: 1px solid $borderDefault;
  }
}
.simulation-sales-result-wdesc {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, max-content));
  align-items: center;
  gap: 4px;
  &__button {
    display: inline-grid;
    place-items: center;
    justify-self: start;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }
}

.simulation-sales-result-popover {
  width: 512px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line;
}

.save {
  width: 100%;
  justify-self: end;
  padding-top: 32px;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid $borderDefault;
  &__text {
    font-weight: 700;
    font-size: 14px;
    color: $semanticError;
  }
}
.dashboard-alert {
  display: grid;
  font-size: 14px;
  font-weight: 700;
  grid-template-columns: 1fr max-content;
  align-items: center;
}
.invisible {
  visibility: hidden;
}
</style>
